import React, { useMemo, useCallback } from 'react';
import { Marker, InfoBox } from '@react-google-maps/api';
import dayjs from 'dayjs';
import { useCookies } from 'react-cookie';
import { Button } from 'src/components';
import { useCreateModal, useEditData } from 'src/stores';
import { IconClose } from 'src/icons';
import { markerBoar, markerDeer, markerBear, markerMonkey, markerOther, 
         catchBear, catchBoar, catchDeer, catchMonkey, catchOther,
         mistakesBear, mistakesBoar, mistakesDeer, mistakesMonkey, mistakesOther, 
         huntBear, huntBoar, huntDeer, huntMonkey, huntOther } from 'src/images';
import { Vermin } from 'src/pages/Home/Home';
import styles from './AnimalMarker.module.scss';
import axios from 'axios';
import { markerRaccoon, catchRaccoon, mistakesRaccoon, huntRaccoon, markerPalmcivet, catchPalmcivet, mistakesPalmcivet, huntPalmcivet } from 'src/images';/*231004追加*/

type Props = {
  vermin: Vermin;
  activeVerminId: number | null;
  firstkind: string | null;
  activeMarker: (v: Vermin) => void;
  closeInfoBox: () => void;
};

// InfoBox オプション値
const infoBoxOptions = {
  disableAutoPan: true,
  alignBottom: true,
  maxWidth: 270,
  boxClass: styles.infobox,
};


/**
 * AnimalMarker コンポーネント
 * @returns React.ReactNode
 */
const AnimalMarker: React.FC<Props> = ({ vermin, activeVerminId, firstkind, activeMarker, closeInfoBox}) => {
  /**
   * 害獣座標をメモ化
   */
  const latlng = useMemo(() => new google.maps.LatLng({
    lat: Number(vermin.vermin_latitude),
    lng: Number(vermin.vermin_longitude),
  }), [vermin]);

  const [cookie] = useCookies();

  /**
   * vermin_kind_name からマーカー画像をセレクト
   * @example
   * <Marker icon={marketFormat([vermin_kind_id])} ... />
   */
  const markerFormat = useCallback((kindName: number) => {
    let marker;
    switch (kindName) {
      case 1:
        marker = vermin.vermin_ecology_name == "捕獲" ? catchBoar : vermin.vermin_ecology_name == "錯誤捕獲" ? mistakesBoar : vermin.vermin_ecology_name == "狩猟" ? huntBoar : markerBoar;
        break;
      case 2:
        marker = vermin.vermin_ecology_name == "捕獲" ? catchBear : vermin.vermin_ecology_name == "錯誤捕獲" ? mistakesBear : vermin.vermin_ecology_name == "狩猟" ? huntBear : markerBear;
        break;
      case 3:
        marker = vermin.vermin_ecology_name == "捕獲" ? catchDeer : vermin.vermin_ecology_name == "錯誤捕獲" ? mistakesDeer : vermin.vermin_ecology_name == "狩猟" ? huntDeer : markerDeer;
        break;
      case 4:
        marker = vermin.vermin_ecology_name == "捕獲" ? catchMonkey : vermin.vermin_ecology_name == "錯誤捕獲" ? mistakesMonkey : vermin.vermin_ecology_name == "狩猟" ? huntMonkey : markerMonkey;  
        break;
      case 5:
        marker = vermin.vermin_ecology_name == "捕獲" ? catchRaccoon : vermin.vermin_ecology_name == "錯誤捕獲" ? mistakesRaccoon : vermin.vermin_ecology_name == "狩猟" ? huntRaccoon : markerRaccoon;
        break;
      case 6:
        marker = vermin.vermin_ecology_name == "捕獲" ? catchPalmcivet : vermin.vermin_ecology_name == "錯誤捕獲" ? mistakesPalmcivet : vermin.vermin_ecology_name == "狩猟" ? huntPalmcivet : markerPalmcivet;
        break;
      case 7:
        marker = vermin.vermin_ecology_name == "捕獲" ? catchOther : vermin.vermin_ecology_name == "錯誤捕獲" ? mistakesOther : vermin.vermin_ecology_name == "狩猟" ? huntOther : markerOther;
        break;
      default:
        marker = '';
    }
    return marker;
  }, []);

  //頭数情報
  const vermincount = (vermin: any) => {
    axios.post(`${process.env.REACT_APP_DEV_API_URL}/vermins/count`, {
      lat: vermin.vermin_latitude,
      lon: vermin.vermin_longitude,
    }).then(async (data) => {
      if(data.data.total > 0){
        var v_c = document.getElementById("vermin_count");
        if(v_c){
          v_c.innerHTML = "過去2年分の捕獲頭数:" + data.data.total;
        }
      }
    })
  }
  const vermincount2 = (vermin: any) => {
    axios.post(`${process.env.REACT_APP_DEV_API_URL}/vermins/count-vermin`, {
      lat: vermin.vermin_latitude,
      lon: vermin.vermin_longitude,
    }, {
      headers: {
        Authorization: `Bearer ${cookie?.verminData?.api_token}`
      }
    }).then(async (data) => {
      if(data.data.total > 0){
        var v_c = document.getElementById("vermin_count");
        if(v_c){
          v_c.innerHTML = "過去2年分の捕獲頭数:" + data.data.total;
        }
      }
    })
  }

  /**
   * InfoBox を表示するハンドラ
   * @example
   * <Marker onClick={handleMarkerClick} ... />
   */
  const handleMarkerClick = useCallback(() => {
    if(vermin.vermin_ecology_name=="捕獲" || vermin.vermin_ecology_name=="錯誤捕獲"){
      if(cookie?.verminData){
        vermincount2(vermin);
      } else {
        vermincount(vermin);
      }
    }
    activeMarker(vermin);
  }, [activeMarker, vermin]);

  /**
 * 害獣情報修正モーダル を表示するハンドラ
 * @example
 * <Button onClick={handleEditModal} ... />
 */
  const handleEditModal = useCallback(() => {
    setEditVerminData(vermin)
    createModalToggle()
  }, [vermin]);

/**
 * 画像拡大モーダル を表示するハンドラ 20230211追記
 * @example
 * <Button onClick={pictureEditModal} ... />
 */
  const pictureEditModal = useCallback(() => {
    setexpPictureData(vermin.vermin_picture)
    createPictureModalOpen()
  }, [vermin.vermin_picture]);

/**
 * 報告書修正モーダル を表示するハンドラ
 * @example
 * <Button onClick={handleEditModal} ... />
 * 20240411 Add Uduki
 */
    const handleEditReportModal = useCallback(() => {
      axios.post(`${process.env.REACT_APP_DEV_API_URL}/report_data`, {
        report_kind:1,
        information_id:vermin.vermin_id,
      },{
        headers: { 
          Authorization: `Bearer ${cookie?.verminData?.api_token}`
         }}).then((res) => {
          if(res.data.features){
            setEditReportData(res.data.features);
            createReportModalOpen();
          }
        }).catch(async (err) => {
          console.log('axios Error')
        })
    }, [vermin]);

 //クリップボードにコピー関数
const copyToClipboard = useCallback(async () => {
  let fk = ""
  if(firstkind){
    fk ='&SelectAN='+firstkind;
  }
  const url = window.location.origin +"/?corporate="+vermin.client_code+"&S_lat="+vermin.vermin_latitude+"&S_lon="+vermin.vermin_longitude+fk;
  await global.navigator.clipboard.writeText(url);
  alert("URLをコピー")
},[vermin,firstkind]);

/**
  * 情報登録モーダルフラグ 20230211追記
  * 20240411 報告書用モーダルフラグ追加
  */
  const [createModalToggle,createPictureModalOpen,createReportModalOpen] = useCreateModal(state => [state.createModalToggle,state.createPictureModalOpen,state.createReportModalOpen]);

/**
  * 編集モーダル開閉時のデータ格納ストア
  * 20240411 Add Uduki 報告書登録ように追記
  */
  const [setEditVerminData,setexpPictureData,setEditReportData] = useEditData(state => [state.setEditVerminData,state.setexpPictureData,state.setEditReportData]);

  /**
   * 画像のパスを取得 20230211追記
   */
  var image_pase = "";
  if(vermin.vermin_picture){
    image_pase = `${process.env.REACT_APP_DEV_IMAGE}/vermin/${vermin.vermin_picture}`;
  }

  return (
    <>
      <Marker
        position={latlng}
        icon={{
          url: markerFormat(vermin.vermin_kind_id),
          anchor: new google.maps.Point(20, 20),
        }}
        onClick={handleMarkerClick}
        onRightClick={copyToClipboard}/*230707コメアウトのURLボタンの代わりに追加*/
        onDblClick={copyToClipboard}
        zIndex={vermin.vermin_id == activeVerminId ? 100 : 0}
      />
      {vermin.vermin_id == activeVerminId && (
        <InfoBox
          position={latlng}
          options={{
            ...infoBoxOptions,
            pixelOffset: new window.google.maps.Size(-135, -10),
          }}
        >
          <div className={styles.overview}>
            <button
              className={styles.closeButton}
              aria-label="詳細ボックスを閉じる"
              onClick={closeInfoBox}
            >
              <IconClose />
            </button>
            <ul>
              <li>{vermin.vermin_discovery_date && `目撃日: ${dayjs(vermin.vermin_discovery_date).format('YYYY年MM月DD日')}`}</li>
              <li>{vermin.vermin_discovery_time && `目撃時刻: ${dayjs(`${vermin.vermin_discovery_date} ${vermin.vermin_discovery_time}`).format('HH:mm')}`}</li>
              <li>{vermin.vermin_address && `場所: ${vermin.vermin_address}`}</li>
              <li>{vermin.vermin_ecology_name && `情報分類: ${vermin.vermin_ecology_name}`}</li>
              <li>{(vermin.vermin_count != 0 && vermin.vermin_count) && `頭数: ${vermin.vermin_count}頭`}</li>
              <li id="vermin_count"></li>
              <li>{vermin.vermin_herd && `群れ: ${vermin.vermin_herd}`}</li>
              <li>{(vermin.trap_hunting_name != "-" && vermin.trap_hunting_name) && `罠情報: ${vermin.trap_hunting_name}`}</li>
              <li>{(vermin.vermin_picture && <div><p>撮影画像</p><div onClick={pictureEditModal}>
                <img style={{maxHeight:"100px",maxWidth:"100px"}} src={image_pase}></img></div></div>)}</li>
            </ul>
            <div className={`${cookie?.verminData?.user_kind_id > 2 ? styles.row : styles.row2}`} id ="edit_button">
              <Button onClick={handleEditModal} disabled={!cookie?.verminData?.user_kind_id || cookie?.verminData?.user_kind_id == 1 || (cookie?.verminData?.user_kind_id == 2 && cookie?.verminData?.user_info_id !== vermin.update_user_id) || vermin.client_code!==cookie?.verminData?.client_code}>
                情報編集
              </Button>
              <Button onClick={handleEditReportModal} disabled={!cookie?.verminData?.user_kind_id || cookie?.verminData?.user_kind_id < 3  || vermin.client_code!==cookie?.verminData?.client_code}>
                報告書編集
              </Button>
            </div>
            {/*<div className={`${styles.row}`}>
              <Button onClick={copyToClipboard}>
                URLをコピー
              </Button>
            </div>230707コメアウト*/}
          </div>
        </InfoBox>
      )}
    </>
  );
};

export default React.memo(AnimalMarker);
